import { Button, Modal, ModalCloseButton, ModalContent, ModalOverlay, Text } from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, FORM_SUBMITTING_TEXT, MODAL_OVERLAY_DARKNESS } from "../const";
import { ContactFormLayout } from "../forms/ContactFormLayout";
import { ModalFormWrapper, useGetDefaultFormValues, useSaveFormValues } from "../forms/FormComponents";
import { useGetFilteredData } from "../hooks/useGetFilteredData";
import { useGetRegionIDAndParkIDsInRegion } from "../hooks/useGetRegionIDAndParkIDsInRegion";
import { DEFAULT_FIRST_FORM_VALUE, useFilterStore } from "../stores/FilterStore";

export const ContactFormModal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { totalCaravans } = useGetFilteredData();
  const { saveFormData } = useSaveFormValues();
  const { county, bedrooms, minPrice, maxPrice } = useFilterStore(["county", "bedrooms", "minPrice", "maxPrice"]);

  const [regionNumber, parkIds] = useGetRegionIDAndParkIDsInRegion();

  const { mutate, isPending } = useMutation({
    mutationFn: (salesLeadData) => {
      return axios.post(`${API_BASE_URL}/saleslead`, salesLeadData, {
        headers: {
          "Content-Type": "application/json",
        },
      });
    },
    onSuccess: () => {
      if (county === DEFAULT_FIRST_FORM_VALUE) {
        navigate("/areas");
      } else {
        navigate(`/areas/${county}`);
      }
    },
  });

  const { defaultValues } = useGetDefaultFormValues();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues });

  const formatData = (value: string) => (value === DEFAULT_FIRST_FORM_VALUE ? "any" : value);

  const onSubmit = (data: any) => {
    saveFormData(data);

    const formatBedroomData = formatData(bedrooms);
    const formatMinPriceData = formatData(minPrice);
    const formatMaxPriceData = formatData(maxPrice);

    const signUpToEmailValue = data.signUpToEmail ? "Y" : "N";

    const salesLeadData = {
      ...data,
      county,
      optIn: signUpToEmailValue,
      bedrooms: formatBedroomData,
      minPrice: formatMinPriceData,
      maxPrice: formatMaxPriceData,
      region: Number(regionNumber),
      operators: parkIds.join(","),
    };

    mutate(salesLeadData);
  };

  const totalCaravansText = useMemo(() => {
    if (totalCaravans === 1) return `We have found 1 Caravan that matches your search criteria`;
    return `We have found ${totalCaravans} Caravans that match your search criteria`;
  }, [totalCaravans]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg={MODAL_OVERLAY_DARKNESS} />
      <ModalContent borderRadius="10px" maxW="515px">
        <ModalCloseButton size="md" />

        <ModalFormWrapper>
          <Text textAlign="center" fontSize="20px" mt={4}>
            {totalCaravansText}
          </Text>

          <Text textAlign="center" fontSize="25px" maxW="80%" mx="auto">
            Complete your details to request latest stock, offers and more park information
          </Text>

          <form onSubmit={handleSubmit(onSubmit)}>
            <ContactFormLayout register={register} errors={errors} multiRegion />

            <Button
              type="submit"
              variant="yellow"
              fontWeight="bold"
              borderRadius="20px"
              borderColor="gray.200"
              borderWidth="1px"
              mt="10px"
              w="100%"
              isDisabled={isPending}
            >
              {isPending ? FORM_SUBMITTING_TEXT : "Show Results"}
            </Button>
          </form>
        </ModalFormWrapper>
      </ModalContent>
    </Modal>
  );
};
